import { useEffect } from "react";
import type { SessionSummary, VisitSurface } from "@backstory/protocol";
import { formatDuration } from "../lib/format";
import { StripChevron, useStripOverflow } from "./ui";
import { STATUS_LABELS, surfaceHost, surfaceLabel, type StageMode, type SurfaceStatus } from "./surfaces";

export interface VisitSurfaceRailProps {
  surfaces: VisitSurface[];
  /** Absolute epoch ms of the shared visit clock. */
  nowAbs: number;
  visitStart: number;
  visitEnd: number;
  statusOf: (surface: VisitSurface) => SurfaceStatus;
  /** Surface driving DevTools, the event list, and citations. */
  primaryId: number;
  secondaryId?: number;
  pinnedId?: number | null;
  mode: StageMode;
  onModeChange: (mode: StageMode) => void;
  onSelect: (surface: VisitSurface) => void;
  onPin: (surfaceId: number | null) => void;
  /** Absolute trigger times per segment, drawn as marks on each surface's track. */
  triggers?: Record<string, number[]>;
  onJumpTo?: (surface: VisitSurface, absTs: number) => void;
  /** Jump to the next error or alarm anywhere in the visit. */
  onNextTrigger?: () => void;
  hasNextTrigger?: boolean;
}

const pct = (ts: number, start: number, end: number) => `${Math.min(100, Math.max(0, ((ts - start) / Math.max(1, end - start)) * 100))}%`;

function segmentSpan(seg: SessionSummary): { start: number; end: number } {
  const start = new Date(seg.startedAt).getTime();
  return { start, end: Math.max(start + 1, new Date(seg.lastEventAt).getTime()) };
}

/**
 * The surfaces of a visit on one shared timeline: a tile per browsing context
 * with its live state, its recorded stretches, and its errors. Clicking a tile
 * moves the stage to that surface at the current moment; clicking a stretch
 * moves the whole visit clock to that point in time.
 */
export function VisitSurfaceRail({ surfaces, nowAbs, visitStart, visitEnd, statusOf, primaryId, secondaryId, pinnedId, mode, onModeChange, onSelect, onPin, triggers = {}, onJumpTo, onNextTrigger, hasNextTrigger }: VisitSurfaceRailProps) {
  const { ref: listRef, more, nudge, center } = useStripOverflow<HTMLOListElement>([surfaces.length]);
  // A visit with many surfaces scrolls, and the one on the stage is often past
  // the edge: the rail would otherwise read as though nothing were playing.
  useEffect(() => {
    center(listRef.current?.querySelector<HTMLElement>(`[data-testid="surface-tile-${primaryId}"]`) ?? null);
  }, [primaryId, center, listRef]);

  if (surfaces.length === 0) return null;
  const single = surfaces.length === 1;

  return (
    <section className="surface-rail card" aria-label="Surfaces in this visit">
      <header>
        <h2>{single ? "1 surface" : `${surfaces.length} surfaces`}</h2>
        <div className="stage-mode" role="group" aria-label="Stage layout">
          {!single && (
            <>
              <button className="btn sm" aria-pressed={mode === "auto"} onClick={() => onModeChange("auto")} title="Follow the surface the user was working in, and show two side by side when both were on screen">
                Auto-follow
              </button>
              <button className="btn sm" aria-pressed={mode === "single"} onClick={() => onModeChange("single")} title="Show only the selected surface">
                One surface
              </button>
            </>
          )}
          {onNextTrigger && (
            <button className="btn sm" disabled={!hasNextTrigger} onClick={onNextTrigger} title="Jump to the next error or alarm anywhere in this visit">
              Next trigger ⏭
            </button>
          )}
        </div>
      </header>

      {/* Chevrons beside the strip rather than over it, as on the DevTools tabs:
          an arrow that covers the first tile hides what it is meant to reach. */}
      <div className="surface-strip">
        {more.start && <StripChevron back className="surface-scroll" label="Scroll surfaces" onClick={() => nudge(-1)} />}
        <ol className="surface-list" ref={listRef}>
          {surfaces.map((sf) => {
            const status = statusOf(sf);
            const role = sf.surfaceId === primaryId ? "primary" : sf.surfaceId === secondaryId ? "secondary" : undefined;
            return (
              <li key={sf.surfaceId}>
                <div className={`surface-tile status-${status}`} data-role={role} data-testid={`surface-tile-${sf.surfaceId}`} aria-current={role === "primary" ? "true" : undefined}>
                  <button
                    className="surface-open"
                    onClick={() => onSelect(sf)}
                    aria-label={`Play surface ${sf.surfaceId + 1}: ${surfaceLabel(sf)}, ${STATUS_LABELS[status]}`}
                    title={[sf.url, `${STATUS_LABELS[status]} at this moment`, `${sf.segments.length === 1 ? "1 segment" : `${sf.segments.length} segments`} · ${formatDuration(sf.durationMs)}`, sf.errorCount > 0 ? `${sf.errorCount} errors` : ""].filter(Boolean).join("\n")}
                  >
                    <span className="surface-name">
                      <span className="dot" aria-hidden="true" />
                      {surfaceLabel(sf)}
                    </span>
                    {/* One line, because height here is height taken from the replay. */}
                    <span className="surface-meta muted small">
                      {surfaceHost(sf) !== surfaceLabel(sf) && <span>{surfaceHost(sf)} ·</span>}
                      <span>{STATUS_LABELS[status]}</span>
                      <span>· {formatDuration(sf.durationMs)}</span>
                      {sf.segments.length > 1 && <span>· {sf.segments.length} segments</span>}
                      {sf.errorCount > 0 && <span className="badge danger">{sf.errorCount} errors</span>}
                      {role && <span className="badge neutral">{role === "primary" ? "playing" : "beside it"}</span>}
                    </span>
                  </button>
                  <div className="surface-track" aria-hidden="true">
                    {sf.segments.map((seg) => {
                      const { start, end } = segmentSpan(seg);
                      return (
                        <button
                          key={seg.id}
                          className="surface-seg"
                          style={{ left: pct(start, visitStart, visitEnd), width: `calc(${pct(end, visitStart, visitEnd)} - ${pct(start, visitStart, visitEnd)})` }}
                          title={`${new Date(seg.startedAt).toLocaleTimeString()} · ${formatDuration(seg.durationMs)} · ${seg.eventCount} events`}
                          onClick={() => onJumpTo?.(sf, start)}
                          tabIndex={-1}
                          data-testid="surface-segment"
                        >
                          {(triggers[seg.id] ?? []).map((ts, i) => (
                            <i key={i} className="trig" style={{ left: pct(ts, start, end) }} />
                          ))}
                        </button>
                      );
                    })}
                    <span className="playhead" style={{ left: pct(nowAbs, visitStart, visitEnd) }} />
                  </div>
                  {!single && (
                    <button
                      className="btn sm ghost surface-pin"
                      aria-pressed={pinnedId === sf.surfaceId}
                      onClick={() => onPin(pinnedId === sf.surfaceId ? null : sf.surfaceId)}
                      title={pinnedId === sf.surfaceId ? "Unpin this surface" : "Keep this surface on the stage"}
                    >
                      {pinnedId === sf.surfaceId ? "Pinned" : "Pin"}
                    </button>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
        {more.end && <StripChevron className="surface-scroll" label="Scroll surfaces" onClick={() => nudge(1)} />}
      </div>
    </section>
  );
}
